import React, { useMemo, useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, TrendingUp, ExternalLink, GitCompareArrows, Swords } from 'lucide-react';
import { apiService } from '@/services/api';
import type { DivisionPlayer, TableFilters, PeriodGameStat } from '@/types/api';
import { SortOption } from '@/types/api';
import { PlayerHistoryModal } from './PlayerHistoryModal';
import { ComparePlayersModal } from './ComparePlayersModal';
import { TeamCompareModal } from './TeamCompareModal';
import HeadToHeadModal from './HeadToHeadModal';

interface PlayerTableProps {
  players: DivisionPlayer[];
  filters: TableFilters;
  onFiltersChange: (filters: TableFilters) => void;
  activeGameTab: string;
  divisionId?: string;
  periodStats?: Record<string, Record<string, PeriodGameStat>>;
}

interface TeamGroup {
  teamName: string;
  players: DivisionPlayer[];
}

interface ComparePlayerOption {
  id: string;
  name: string;
}

const GAME_SORT_MAP: Record<string, { csr: SortOption; cls: SortOption; local: SortOption; national: SortOption; matches: SortOption }> = {
  '8ball': { csr: SortOption.EIGHTBALL_CSR, cls: SortOption.EIGHTBALL_CLASS, local: SortOption.EIGHTBALL_RANK, national: SortOption.EIGHTBALL_NATIONAL, matches: SortOption.MATCHES_8BALL },
  '9ball': { csr: SortOption.NINEBALL_CSR, cls: SortOption.NINEBALL_CLASS, local: SortOption.NINEBALL_RANK, national: SortOption.NINEBALL_NATIONAL, matches: SortOption.MATCHES_9BALL },
  '10ball': { csr: SortOption.TENBALL_CSR, cls: SortOption.TENBALL_CLASS, local: SortOption.TENBALL_RANK, national: SortOption.TENBALL_NATIONAL, matches: SortOption.MATCHES_10BALL },
};

const GAME_TYPE_LABELS: Record<string, string> = {
  '8ball': '8-ball',
  '9ball': '9-ball',
  '10ball': '10-ball',
};

export default function PlayerTable({ players, filters, onFiltersChange, activeGameTab, divisionId, periodStats }: PlayerTableProps) {
  const [historyModal, setHistoryModal] = useState<{
    isOpen: boolean;
    playerId: string;
    playerName: string;
    gameType: '8-ball' | '9-ball' | '10-ball';
  }>({ isOpen: false, playerId: '', playerName: '', gameType: '8-ball' });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showCompare, setShowCompare] = useState(false);
  const [showHeadToHead, setShowHeadToHead] = useState(false);
  const [teamCompare, setTeamCompare] = useState<{ players: { id: string; name: string }[]; teamName: string } | null>(null);
  const [teamVsTeam, setTeamVsTeam] = useState<{
    teamA: { name: string; players: DivisionPlayer[] };
    teamB: { name: string; players: DivisionPlayer[] };
  } | null>(null);
  const [teamPickerFor, setTeamPickerFor] = useState<{ name: string; players: DivisionPlayer[] } | null>(null);

  const gameLabel = GAME_TYPE_LABELS[activeGameTab] || '8-ball';

  const toggleSelect = (playerId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === players.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(players.map((p) => p.player_id)));
    }
  };

  const comparePlayers = useMemo(() => {
    return players
      .filter((p) => selectedIds.has(p.player_id))
      .map((p) => ({ id: p.player_id, name: p.name }));
  }, [players, selectedIds]);

  const allComparePlayers = useMemo<ComparePlayerOption[]>(
    () => players.map((player) => ({ id: player.player_id, name: player.name })),
    [players]
  );

  // H2H pair: resolve two selected players for Head-to-Head
  const h2hPair = useMemo(() => {
    if (selectedIds.size !== 2) return null;
    const ids = Array.from(selectedIds);
    const a = players.find((p) => p.player_id === ids[0]);
    const b = players.find((p) => p.player_id === ids[1]);
    if (!a || !b) return null;
    return { playerA: { id: a.player_id, name: a.name }, playerB: { id: b.player_id, name: b.name } };
  }, [players, selectedIds]);

  const handleSort = (sortOption: SortOption) => {
    const newDirection = filters.sortBy === sortOption && filters.sortDirection === 'desc' ? 'asc' : 'desc';
    onFiltersChange({ ...filters, sortBy: sortOption, sortDirection: newDirection });
  };

  const SortIcon = ({ option }: { option: SortOption }) => {
    if (filters.sortBy !== option) return <ArrowUpDown className="w-3 h-3 text-slate-300 md:opacity-0 md:group-hover:opacity-100 transition-opacity" />;
    return filters.sortDirection === 'desc'
      ? <ArrowDown className="w-3 h-3 text-brand-500" />
      : <ArrowUp className="w-3 h-3 text-brand-500" />;
  };

  const SortHeader = ({ label, option }: { label: string; option: SortOption }) => (
    <button
      onClick={() => handleSort(option)}
      className="group inline-flex items-center gap-1 hover:text-slate-900 active:text-slate-900 transition-colors"
    >
      <span>{label}</span>
      <SortIcon option={option} />
    </button>
  );

  const renderPlayerCard = (player: DivisionPlayer) => {
    const stats = player.games[activeGameTab];
    const isSelected = selectedIds.has(player.player_id);
    const periodCsr = periodStats?.[player.player_id]?.[activeGameTab]?.csr;
    const showPeriodCsr = periodStats && periodCsr != null;
    const csr = showPeriodCsr ? periodCsr : stats?.skill_level;
    return (
      <div
        key={player.player_id}
        className={`border border-slate-200 rounded-xl p-3 transition-colors ${isSelected ? 'bg-brand-50/60 border-brand-300' : 'bg-white'}`}
      >
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelect(player.player_id)}
            className="mt-1 w-5 h-5 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-slate-900 truncate">{player.name}</span>
              {csr ? (
                <span className={`flex-shrink-0 inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full text-sm font-bold ${showPeriodCsr ? 'bg-amber-50 text-amber-700' : 'bg-brand-50 text-brand-700'}`}>
                  {csr}
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
              {!filters.groupByTeam && player.team_name && (
                <span className="truncate">{player.team_name}</span>
              )}
              {stats?.skill_class && <span>Class {stats.skill_class}</span>}
              {stats?.matches_played != null && <span>{stats.matches_played} matches</span>}
            </div>
            {stats?.wins != null && (
              <div className="flex items-center gap-3 mt-1.5 text-xs">
                <span>
                  <span className="text-emerald-600 font-semibold">{stats.wins}W</span>
                  <span className="text-slate-300 mx-0.5">/</span>
                  <span className="text-red-500 font-semibold">{stats.losses ?? 0}L</span>
                </span>
                {stats.win_percentage != null && (
                  <span className={`font-semibold ${stats.win_percentage >= 50 ? 'text-emerald-600' : 'text-slate-500'}`}>
                    {Math.round(stats.win_percentage)}%
                  </span>
                )}
                {stats.local_rank && <span className="text-slate-400">Local #{stats.local_rank}</span>}
              </div>
            )}
          </div>
          <button
            onClick={() => setHistoryModal({
              isOpen: true,
              playerId: player.player_id,
              playerName: player.name,
              gameType: gameLabel as '8-ball' | '9-ball' | '10-ball',
            })}
            className="p-2 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-brand-50 active:bg-brand-100 transition-colors flex-shrink-0"
            title="View History"
          >
            <TrendingUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const teamGroups = useMemo((): TeamGroup[] | null => {
    if (!filters.groupByTeam) return null;
    const teams = new Map<string, DivisionPlayer[]>();
    players.forEach((p) => {
      const t = p.team_name || 'No Team';
      if (!teams.has(t)) teams.set(t, []);
      teams.get(t)!.push(p);
    });
    return Array.from(teams.entries())
      .map(([teamName, players]) => ({ teamName, players }))
      .sort((a, b) => a.teamName.localeCompare(b.teamName));
  }, [players, filters.groupByTeam]);

  const sorts = GAME_SORT_MAP[activeGameTab];
  const colCount = filters.groupByTeam ? 10 : 11;

  const renderPlayerRow = (player: DivisionPlayer) => {
    const stats = player.games[activeGameTab];
    const isSelected = selectedIds.has(player.player_id);
    const periodCsr = periodStats?.[player.player_id]?.[activeGameTab]?.csr;
    const showPeriodCsr = periodStats && periodCsr != null;
    return (
      <tr
        key={player.player_id}
        onClick={() => setHistoryModal({
          isOpen: true,
          playerId: player.player_id,
          playerName: player.name,
          gameType: gameLabel as '8-ball' | '9-ball' | '10-ball',
        })}
        className={`table-row-hover border-b border-slate-100 last:border-0 cursor-pointer ${isSelected ? 'bg-brand-50/40' : ''}`}
      >
        {/* Checkbox */}
        <td className="pl-4 pr-1 py-3 w-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelect(player.player_id)}
            onClick={(e) => e.stopPropagation()}
            className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
          />
        </td>
        {/* Player */}
        <td className="px-4 py-3 text-left">
          <div className="font-medium text-slate-900">{player.name}</div>
          <a
            href={apiService.getPlayerProfileUrl(player.player_id)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-brand-600 transition-colors"
          >
            #{player.player_id}
            <ExternalLink className="w-3 h-3" />
          </a>
        </td>
        {/* Team */}
        {!filters.groupByTeam && (
          <td className="px-4 py-3 text-left text-sm text-slate-600">
            {player.team_name || <span className="text-slate-300">—</span>}
          </td>
        )}
        {/* CSR — show period CSR when viewing a historical division */}
        <td className="px-4 py-3 text-center">
          {showPeriodCsr ? (
            <span
              className="inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-sm font-bold"
              title={`Season-end CSR (current: ${stats?.skill_level ?? '—'})`}
            >
              {periodCsr}
            </span>
          ) : stats?.skill_level ? (
            <span className="inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 text-sm font-bold">
              {stats.skill_level}
            </span>
          ) : <span className="text-slate-300">—</span>}
        </td>
        {/* Class */}
        <td className="px-4 py-3 text-center text-sm text-slate-600">{stats?.skill_class || <span className="text-slate-300">—</span>}</td>
        {/* Local Rank */}
        <td className="px-4 py-3 text-center text-sm">
          {stats?.local_rank ? <span className="text-slate-700 font-medium">#{stats.local_rank}</span> : <span className="text-slate-300">—</span>}
        </td>
        {/* National Rank */}
        <td className="px-4 py-3 text-center text-sm">
          {stats?.national_rank ? <span className="text-slate-500">#{stats.national_rank}</span> : <span className="text-slate-300">—</span>}
        </td>
        {/* Matches */}
        <td className="px-4 py-3 text-center text-sm text-slate-600">{stats?.matches_played || <span className="text-slate-300">—</span>}</td>
        {/* W/L */}
        <td className="px-4 py-3 text-center text-sm">
          {stats?.wins != null ? (
            <span className="text-slate-600">
              <span className="text-emerald-600 font-medium">{stats.wins}</span>
              <span className="text-slate-300 mx-0.5">/</span>
              <span className="text-red-500 font-medium">{stats.losses ?? 0}</span>
            </span>
          ) : <span className="text-slate-300">—</span>}
        </td>
        {/* Win % */}
        <td className="px-4 py-3 text-center text-sm">
          {stats?.win_percentage != null ? (
            <span className={`font-medium ${stats.win_percentage >= 50 ? 'text-emerald-600' : 'text-slate-500'}`}>
              {Math.round(stats.win_percentage)}%
            </span>
          ) : <span className="text-slate-300">—</span>}
        </td>
        {/* History */}
        <td className="px-4 py-3 text-center">
          <button
            onClick={() => setHistoryModal({
              isOpen: true,
              playerId: player.player_id,
              playerName: player.name,
              gameType: gameLabel as '8-ball' | '9-ball' | '10-ball',
            })}
            className="p-1.5 rounded-md text-slate-400 hover:text-brand-600 hover:bg-brand-50 active:bg-brand-100 transition-colors"
            title="View History"
          >
            <TrendingUp className="w-4 h-4" />
          </button>
        </td>
      </tr>
    );
  };

  if (players.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
        <p className="text-slate-400 text-lg">No players found</p>
        <p className="text-slate-300 text-sm mt-1">Try a different search or division.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm flex-1 min-h-0 flex flex-col">
        {/* Mobile card view */}
        <div className="md:hidden flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedIds.size === players.length && players.length > 0}
                ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < players.length; }}
                onChange={toggleSelectAll}
                className="w-5 h-5 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
              />
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {players.length} players
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <span>Sort:</span>
              <SortHeader label="CSR" option={sorts.csr} />
              <span className="text-slate-300">|</span>
              <SortHeader label="W/L" option={sorts.matches} />
              <span className="text-slate-300">|</span>
              <SortHeader label="Name" option={SortOption.NAME} />
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-2">
            {teamGroups ? (
              teamGroups.map((group) => (
                <React.Fragment key={group.teamName}>
                  <div className="px-2 pt-2 pb-1 flex items-center justify-between">
                    <span className="text-sm font-semibold text-brand-800">
                      🏆 {group.teamName}
                      <span className="ml-2 text-brand-400 font-normal text-xs">{group.players.length} players</span>
                    </span>
                    {group.players.length >= 2 && (
                      <button
                        onClick={() => setTeamCompare({
                          players: group.players.map((p) => ({ id: p.player_id, name: p.name })),
                          teamName: group.teamName,
                        })}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-100 active:bg-brand-200 rounded-md transition-colors"
                      >
                        <GitCompareArrows className="w-3.5 h-3.5" />
                        Compare
                      </button>
                    )}
                  </div>
                  {group.players.map(renderPlayerCard)}
                </React.Fragment>
              ))
            ) : (
              players.map(renderPlayerCard)
            )}
          </div>
        </div>

        {/* Desktop table view */}
        <div className="hidden md:flex md:flex-1 md:min-h-0 md:flex-col overflow-auto">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-slate-200 bg-slate-50">
                {/* Select all checkbox */}
                <th className="pl-4 pr-1 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === players.length && players.length > 0}
                    ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < players.length; }}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-52">
                  <SortHeader label="Player" option={SortOption.NAME} />
                </th>
                {!filters.groupByTeam && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-44">
                    <SortHeader label="Team" option={SortOption.TEAM} />
                  </th>
                )}
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">
                  {periodStats ? (
                    <span className="text-amber-600" title="Showing CSR from this season (not current)">
                      <SortHeader label="CSR ⏱" option={sorts.csr} />
                    </span>
                  ) : (
                    <SortHeader label="CSR" option={sorts.csr} />
                  )}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">
                  <SortHeader label="Class" option={sorts.cls} />
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">
                  <SortHeader label="Local" option={sorts.local} />
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">
                  <SortHeader label="National" option={sorts.national} />
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">
                  <SortHeader label="Matches" option={sorts.matches} />
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">W/L</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">Win%</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-14"></th>
              </tr>
            </thead>
            <tbody>
              {teamGroups ? (
                teamGroups.map((group) => (
                  <React.Fragment key={group.teamName}>
                    <tr className="bg-brand-50/50">
                      <td colSpan={colCount} className="px-4 py-2.5 text-sm font-semibold text-brand-800">
                        <div className="flex items-center justify-between">
                          <span>
                            🏆 {group.teamName}
                            <span className="ml-2 text-brand-400 font-normal text-xs">{group.players.length} players</span>
                          </span>
                          <div className="flex items-center gap-2">
                            {group.players.length >= 2 && (
                              <button
                                onClick={() => setTeamCompare({
                                  players: group.players.map((p) => ({ id: p.player_id, name: p.name })),
                                  teamName: group.teamName,
                                })}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-100 active:bg-brand-200 rounded-md transition-colors"
                                title={`Compare all ${group.teamName} players`}
                              >
                                <GitCompareArrows className="w-3.5 h-3.5" />
                                Compare
                              </button>
                            )}
                            {teamGroups && teamGroups.length >= 2 && (
                              <button
                                onClick={() => setTeamPickerFor({ name: group.teamName, players: group.players })}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 hover:text-brand-700 hover:bg-brand-100 active:bg-brand-200 rounded-md transition-colors"
                                title={`Compare ${group.teamName} vs another team`}
                              >
                                vs Team
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                    {group.players.map(renderPlayerRow)}
                  </React.Fragment>
                ))
              ) : (
                players.map(renderPlayerRow)
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <span className="text-xs text-slate-400">
            {players.length} player{players.length !== 1 ? 's' : ''}
            {teamGroups && ` across ${teamGroups.length} team${teamGroups.length !== 1 ? 's' : ''}`}
          </span>
          {selectedIds.size > 0 && (
            <span className="text-xs text-brand-600 font-medium">{selectedIds.size} selected</span>
          )}
        </div>
      </div>

      {/* Floating compare button */}
      {selectedIds.size >= 2 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex flex-col md:flex-row items-center gap-2 md:gap-1.5">
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowCompare(true)}
              className="flex items-center gap-2 px-5 py-3 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white rounded-full shadow-lg shadow-brand-600/30 font-semibold text-sm transition-all hover:scale-105"
            >
            <GitCompareArrows className="w-4 h-4" />
            Compare {selectedIds.size} Players
          </button>
          {selectedIds.size === 2 && (
            <button
              onClick={() => setShowHeadToHead(true)}
              className="flex items-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white rounded-full shadow-lg shadow-amber-500/30 font-semibold text-sm transition-all hover:scale-105"
            >
              <Swords className="w-4 h-4" />
              Head to Head
            </button>
          )}
          <button
            onClick={() => setSelectedIds(new Set())}
            className="flex items-center justify-center w-8 h-8 bg-gray-800/80 hover:bg-red-600 active:bg-red-700 text-white/70 hover:text-white rounded-full shadow-lg text-xs font-bold transition-all hover:scale-110"
            title="Clear selection"
          >
            ✕
          </button>
          </div>
        </div>
      )}

      {/* History Modal */}
      <PlayerHistoryModal
        isOpen={historyModal.isOpen}
        onClose={() => setHistoryModal({ isOpen: false, playerId: '', playerName: '', gameType: '8-ball' })}
        playerId={historyModal.playerId}
        playerName={historyModal.playerName}
        initialGameType={historyModal.gameType}
        divisionId={divisionId}
        divisionPlayers={players}
      />

      {/* Compare Modal (manual selection) */}
      <ComparePlayersModal
        isOpen={showCompare}
        onClose={() => setShowCompare(false)}
        players={comparePlayers}
        availablePlayers={allComparePlayers}
        onPlayersChange={(nextPlayers) => setSelectedIds(new Set(nextPlayers.map((player) => player.id)))}
        initialGameType={gameLabel as '8-ball' | '9-ball' | '10-ball'}
        divisionId={divisionId}
        divisionPlayers={players}
      />

      {/* Head to Head Modal */}
      {h2hPair && (
        <HeadToHeadModal
          isOpen={showHeadToHead}
          onClose={() => setShowHeadToHead(false)}
          playerAId={h2hPair.playerA.id}
          playerAName={h2hPair.playerA.name}
          playerBId={h2hPair.playerB.id}
          playerBName={h2hPair.playerB.name}
          initialGameType={gameLabel as '8-ball' | '9-ball' | '10-ball'}
          divisionId={divisionId}
        />
      )}

      {/* Compare Modal (team-scoped) */}
      {teamCompare && (
        <ComparePlayersModal
          isOpen={true}
          onClose={() => setTeamCompare(null)}
          players={teamCompare.players}
          availablePlayers={teamCompare.players}
          onPlayersChange={(nextPlayers) => {
            setTeamCompare((current) => (current ? { ...current, players: nextPlayers } : current));
          }}
          initialGameType={gameLabel as '8-ball' | '9-ball' | '10-ball'}
          teamName={teamCompare.teamName}
          divisionId={divisionId}
          divisionPlayers={players}
        />
      )}

      {/* Team picker dropdown */}
      {teamPickerFor && teamGroups && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4" onClick={() => setTeamPickerFor(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-slate-900 mb-1">Compare Teams</h3>
            <p className="text-sm text-slate-400 mb-4">
              Select a team to compare against <span className="font-medium text-brand-600">{teamPickerFor.name}</span>
            </p>
            <div className="space-y-2">
              {teamGroups
                .filter(g => g.teamName !== teamPickerFor.name)
                .map(g => (
                  <button
                    key={g.teamName}
                    onClick={() => {
                      setTeamVsTeam({
                        teamA: { name: teamPickerFor.name, players: teamPickerFor.players },
                        teamB: { name: g.teamName, players: g.players },
                      });
                      setTeamPickerFor(null);
                    }}
                    className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 hover:border-brand-300 hover:bg-brand-50/50 transition-all group"
                  >
                    <span className="font-medium text-slate-900 group-hover:text-brand-700">{g.teamName}</span>
                    <span className="text-xs text-slate-400 ml-2">{g.players.length} players</span>
                  </button>
                ))}
            </div>
            <button
              onClick={() => setTeamPickerFor(null)}
              className="mt-4 w-full px-4 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Team vs Team Compare Modal */}
      {teamVsTeam && (
        <TeamCompareModal
          isOpen={true}
          onClose={() => setTeamVsTeam(null)}
          teamA={teamVsTeam.teamA}
          teamB={teamVsTeam.teamB}
          activeGameTab={activeGameTab}
          divisionId={divisionId}
        />
      )}
    </>
  );
}
